import { type MailContent, mailText, renderMail } from '@ensemble.music/email'
import { serverEnv } from './env'
import { centsToEuro } from './sla'

type Mail = MailContent & { to: string; subject: string }

// Resend when a key is configured, otherwise the message is logged. That keeps local work and
// tests out of real inboxes without a second code path for "is this a test".
async function send(mail: Mail): Promise<'sent' | 'logged' | 'failed'> {
  const env = serverEnv()
  const from = env.MAIL_FROM
  if (!env.RESEND_API_KEY || !from) {
    console.info('[mail:logged]', mail.subject, '->', mail.to, '\n', mailText(mail))
    return 'logged'
  }
  try {
    const { html, text } = await renderMail(mail)
    const response = await fetch('https://api.resend.com/emails', {
      method: 'POST',
      headers: {
        authorization: `Bearer ${env.RESEND_API_KEY}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify({ from, to: [mail.to], subject: mail.subject, html, text }),
    })
    if (!response.ok) {
      console.error('[mail:failed]', response.status, await response.text())
      return 'failed'
    }
    return 'sent'
  } catch (error) {
    console.error('[mail:failed]', error)
    return 'failed'
  }
}

async function push(title: string, body: string) {
  const env = serverEnv()
  if (!env.NTFY_TOPIC) return
  try {
    await fetch(`https://ntfy.sh/${env.NTFY_TOPIC}`, {
      method: 'POST',
      headers: { title, priority: 'default' },
      body,
    })
  } catch (error) {
    // a missing push must never fail the request - the email is the channel that counts
    console.error('[ntfy:failed]', error)
  }
}

// Every founder alert: email when FOUNDER_EMAIL is set, otherwise logged; ntfy on top if configured.
async function alertFounder(subject: string, pushTitle: string, content: MailContent) {
  const env = serverEnv()
  const text = mailText(content)
  let mail: Awaited<ReturnType<typeof send>> = 'logged'
  if (env.FOUNDER_EMAIL) {
    mail = await send({ to: env.FOUNDER_EMAIL, subject, ...content })
  } else {
    console.info('[alert:logged] FOUNDER_EMAIL is unset:', subject, '\n', text)
  }
  await push(pushTitle, text)
  return mail
}

export type RequestSummary = {
  id: string
  bookerName: string
  bookerEmail: string
  occasion: string
  city: string
  eventDate: Date
  budgetMinCents: number | null
  budgetMaxCents: number | null
  slaDueAt: Date
}

// Vercel runs in UTC; without the zone a deadline in an email is one or two hours off
const timeZone = 'Europe/Berlin'
const de = (date: Date) => date.toLocaleDateString('de-DE', { dateStyle: 'medium', timeZone })
const deTime = (date: Date) =>
  date.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short', timeZone })

const adminRequestUrl = (id: string) => `${serverEnv().NEXT_PUBLIC_SITE_URL}/admin/requests/${id}`

export async function notifyFounder(request: RequestSummary) {
  const budget =
    request.budgetMinCents === null && request.budgetMaxCents === null
      ? 'kein Budget angegeben'
      : `${request.budgetMinCents === null ? '?' : centsToEuro(request.budgetMinCents)} bis ${
          request.budgetMaxCents === null ? '?' : centsToEuro(request.budgetMaxCents)
        }`
  const subject = `Neue Anfrage: ${request.occasion}`
  return alertFounder(subject, 'Neue Anfrage', {
    heading: 'Neue Anfrage',
    body: [
      `Von ${request.bookerName} (${request.bookerEmail})`,
      [
        `Anlass: ${request.occasion}`,
        `Termin: ${de(request.eventDate)} in ${request.city}`,
        `Budget: ${budget}`,
        `Antwort fällig bis: ${deTime(request.slaDueAt)}`,
      ].join('\n'),
    ],
    action: { label: 'Anfrage öffnen', url: adminRequestUrl(request.id) },
  })
}

export type EnsembleSummary = {
  id: string
  name: string
  contactName: string
  email: string
  region: string
  lineupType: string
  priceFromCents: number
  videoUrl: string | null
  hasOffer: boolean
}

// Ensemble signup (S0-013/S0-014): one alert per new ensemble, same channels as a new request.
export async function notifyFounderOfEnsemble(ensemble: EnsembleSummary) {
  const env = serverEnv()
  const subject = `Neues Ensemble: ${ensemble.name}`
  return alertFounder(subject, 'Neues Ensemble', {
    heading: subject,
    body: [
      `${ensemble.contactName}, ${ensemble.email}`,
      [
        `Region: ${ensemble.region}`,
        `Besetzung: ${ensemble.lineupType}, ab ${centsToEuro(ensemble.priceFromCents)}`,
        `Video: ${ensemble.videoUrl ?? 'keins angegeben'}`,
        `Gig eingetragen: ${ensemble.hasOffer ? 'ja' : 'nein'}`,
      ].join('\n'),
    ],
    // shortcut: no ensemble detail page in /admin yet, link the overview until one exists
    action: { label: 'Adminbereich öffnen', url: `${env.NEXT_PUBLIC_SITE_URL}/admin` },
  })
}

// Ensemble signup confirmation (S0-013). Ensembles are addressed with "ihr", like the signup form.
export async function sendEnsembleConfirmation(ensemble: EnsembleSummary) {
  return send({
    to: ensemble.email,
    subject: 'Eure Anmeldung bei ensemble ist angekommen',
    heading: 'Eure Anmeldung ist angekommen',
    body: [
      `Hallo ${ensemble.contactName},`,
      `danke, dass ${ensemble.name} bei ensemble dabei sein möchte.`,
      `${
        ensemble.hasOffer
          ? 'Euren eingetragenen Gig schauen wir uns an und nehmen ihn ins Angebot auf, sobald er passt.'
          : 'Sobald es eine Anfrage gibt, die zu euch passt, melden wir uns bei euch.'
      } Ihr bekommt von uns nur Anfragen, die zu Region, Besetzung und Preis passen.`,
      'Viele Grüße\nensemble',
    ],
    footer: ['Wenn sich etwas an euren Angaben ändert, antwortet einfach auf diese E-Mail.'],
  })
}

export async function sendBookerConfirmation(request: RequestSummary) {
  return send({
    to: request.bookerEmail,
    subject: 'Deine Anfrage bei ensemble ist angekommen',
    heading: 'Deine Anfrage ist angekommen',
    body: [
      `Hallo ${request.bookerName},`,
      `danke für deine Anfrage für ${request.occasion} am ${de(request.eventDate)} in ${request.city}.`,
      'So geht es weiter: Wir suchen passende Ensembles und melden uns innerhalb von 48 Stunden mit bis zu drei Angeboten bei dir. Du entscheidest dann in Ruhe.',
      'Viele Grüße\nensemble',
    ],
    footer: ['Wenn sich etwas an deinen Angaben ändert, antworte einfach auf diese E-Mail.'],
  })
}

// Admin login (S0-015). German, informal "du" - the only recipient is the founder.
export async function sendAdminMagicLink(to: string, url: string) {
  return send({
    to,
    subject: 'Dein Login-Link für ensemble',
    heading: 'Dein Login-Link',
    body: ['Hier ist dein Login-Link für den ensemble-Adminbereich.'],
    action: { label: 'Jetzt anmelden', url },
    footer: [
      'Der Link gilt 10 Minuten und funktioniert einmal. Wenn du dich nicht anmelden wolltest, ignoriere diese Mail einfach.',
    ],
  })
}

// Booker and ensemble login (S0-014): one mail per area the address belongs to, one button each. The
// plaintext token exists only in this mail - the database stores its hash.
export async function sendLoginLink(
  to: string,
  area: { role: 'booker' | 'ensemble'; label: string; url: string; validUntil: Date }
) {
  const ensemble = area.role === 'ensemble'
  return send({
    to,
    subject: 'Dein Login-Link für ensemble',
    heading: 'Dein Login-Link',
    body: [
      ensemble
        ? `Hier kommst du in den Ensemblebereich von ${area.label}.`
        : `Hier kommst du zu deiner Anfrage: ${area.label}.`,
    ],
    action: { label: 'Jetzt anmelden', url: area.url },
    footer: [
      `Der Link gilt bis ${deTime(area.validUntil)} und ist nur für dich. Bitte leite ihn nicht weiter.`,
      'Wenn du dich nicht anmelden wolltest, ignoriere diese Mail einfach.',
    ],
  })
}

// Offer composer (S0-018): the booker gets the status link, nothing else. The plaintext token
// exists only in this email - the database stores its hash.
export async function sendOffersToBooker(
  request: Pick<RequestSummary, 'bookerName' | 'bookerEmail' | 'occasion' | 'eventDate' | 'city'>,
  statusUrl: string,
  count: number,
  validUntil: Date
) {
  const subject =
    count === 1
      ? 'Ein Angebot für deine Anfrage ist da'
      : `${count} Angebote für deine Anfrage sind da`
  return send({
    to: request.bookerEmail,
    subject,
    heading: subject,
    body: [
      `Hallo ${request.bookerName},`,
      `für ${request.occasion} am ${de(request.eventDate)} in ${request.city} haben wir ${
        count === 1 ? 'ein passendes Ensemble' : `${count} passende Ensembles`
      } für dich gefunden.`,
      'Preis, Leistungen und Video findest du über den Button - dort kannst du auch direkt zusagen oder absagen.',
    ],
    action: { label: count === 1 ? 'Angebot ansehen' : 'Angebote ansehen', url: statusUrl },
    footer: [
      `Der Link gilt bis ${de(validUntil)} und ist nur für dich. Bitte leite ihn nicht weiter.`,
      'Viele Grüße\nensemble',
    ],
  })
}

export type OfferAnswer = {
  requestId: string
  bookerName: string
  occasion: string
  eventDate: Date
  ensembleName: string
  priceCents: number
  booking: { feeCents: number; invoiceRef: string } | null
  decision: 'accept' | 'decline'
}

export async function notifyFounderOfAnswer(answer: OfferAnswer) {
  const verb = answer.decision === 'accept' ? 'angenommen' : 'abgelehnt'
  const subject = `Angebot ${verb}: ${answer.ensembleName}`
  return alertFounder(subject, `Angebot ${verb}`, {
    heading: `Angebot ${verb}`,
    body: [
      `${answer.bookerName} hat das Angebot von ${answer.ensembleName} (${centsToEuro(answer.priceCents)}) ${verb}.`,
      `Anfrage: ${answer.occasion} am ${de(answer.eventDate)}`,
      ...(answer.booking
        ? [
            [
              'Gebucht. Die anderen Angebote dieser Anfrage sind damit abgelehnt.',
              `Gage: ${centsToEuro(answer.priceCents)}`,
              `Servicegebühr: ${centsToEuro(answer.booking.feeCents)}`,
              `Rechnungsnummer: ${answer.booking.invoiceRef}`,
            ].join('\n'),
            'Jetzt Rechnung und Vertrag von Hand schicken - es läuft keine Zahlung über ensemble.',
          ]
        : []),
    ],
    action: { label: 'Anfrage öffnen', url: adminRequestUrl(answer.requestId) },
  })
}

const slaSubjects = {
  stale: 'Seit 24 h unbearbeitet',
  due_soon: 'Frist endet in 6 h',
  missed: 'Frist verpasst',
} as const

// The SLA cron (S0-020): one reminder per request and stage, deduplicated by the caller.
export async function notifyFounderOfSla(
  request: Pick<
    RequestSummary,
    'id' | 'bookerName' | 'occasion' | 'city' | 'eventDate' | 'slaDueAt'
  >,
  stage: keyof typeof slaSubjects
) {
  const subject = `${slaSubjects[stage]}: ${request.occasion}, ${request.bookerName}`
  return alertFounder(subject, slaSubjects[stage], {
    heading: slaSubjects[stage],
    body: [
      `Frist der 48-Stunden-Zusage an ${request.bookerName}: ${deTime(request.slaDueAt)}`,
      `Anfrage: ${request.occasion} am ${de(request.eventDate)} in ${request.city}`,
    ],
    action: { label: 'Anfrage öffnen', url: adminRequestUrl(request.id) },
  })
}
